import { SupplierFactorDraftReviewState } from 'modules/FreeSupplier/supplierDraftContracts';
import { SupplierSourceUploadValidationState } from 'modules/FreeSupplier/supplierUploadContracts';

// The 9-step getting-started checklist derivation (fixpack item #15), pulled
// out of MockDataContext's `value` memo into a pure function so the signal
// logic is unit-testable without standing up the live-data context (see
// checklist.test.js). Keys here are the exact set GettingStartedPage's STEPS
// array walks, in step order:
//   companySaved -> hasInstallation -> addedDraftValues -> publishedFactor ->
//   editedFactor -> withdrawnFactor -> discardedDraft -> portalShared ->
//   requestHandled
export function deriveSupplierChecklist({
  companySaved,
  installationCount,
  // The flat published-factor read model (data.emissionFactors) — every item
  // carries `revisionNumber` (see supplierFactorReadModel.js).
  emissionFactors = [],
  // GET /supplier/factors top-level `summary` — OPTIONAL, only present once
  // the backend rollout lands (D-15). Absent ⇒ treated as unknown, not false,
  // except where a fallback below can derive the same signal from `items`.
  factorsSummary,
  uploads = [], // supplierUploads?.items
  drafts = [], // supplierDrafts?.items
  portalEnabled,
  requests = [], // flattened S3 inbox requests (connection.requests)
}) {
  const hasPublishedFactor = emissionFactors.length > 0;
  const hasValidatedUpload = uploads.some(
    (upload) => upload.validationState === SupplierSourceUploadValidationState.Validated,
  );
  const hasAnyDraft = drafts.length > 0;
  const hasDiscardedDraft = drafts.some(
    (draft) => draft.reviewState === SupplierFactorDraftReviewState.Discarded,
  );

  // Withdraw has no FE-only fallback: a withdrawn series vanishes entirely
  // from the published-only list, so there's nothing in `emissionFactors` to
  // infer it from. An older backend without `summary` reports false (not
  // "unknown treated as done") rather than guess.
  const hasWithdrawn = Boolean(factorsSummary?.hasWithdrawn);
  // Edit DOES have a fallback — any factor beyond revision 1 has been
  // corrected at least once, derivable from the list itself.
  const hasEdited =
    factorsSummary?.hasEdited ?? emissionFactors.some((factor) => factor.revisionNumber > 1);

  return {
    companySaved: Boolean(companySaved),
    hasInstallation: installationCount > 0,
    // "Add values as drafts" is satisfied by any draft ever created OR a
    // validated upload OR (for a supplier who published directly, skipping
    // drafts entirely) a published factor — any of the three means the
    // supplier has gotten emissions data into the system.
    addedDraftValues: hasAnyDraft || hasValidatedUpload || hasPublishedFactor,
    publishedFactor: hasPublishedFactor,
    editedFactor: hasEdited,
    withdrawnFactor: hasWithdrawn,
    discardedDraft: hasDiscardedDraft,
    portalShared: Boolean(portalEnabled),
    // "Handled" once the supplier has moved a request past pending
    // (accepted / in progress / fulfilled / declined).
    requestHandled: requests.some((request) => request.state !== 'pending'),
  };
}
